/**
 * GutachtenCrossLinkPill — Issue #589
 * ------------------------------------
 * Cross-link pill shown inside the Werkstatt-Akte detail view when the
 * Werkstatt-Akte was created from a Gutachten-Akte (gutachten_akte_id is set).
 *
 * Performs a direct lookup by gutachten_akte_id (forward reference, not a
 * reverse query) and renders a clickable chip:
 *
 *   "Aus Gutachten: AK2602101 · Max Mustermann →"
 *
 * Clicking navigates to the Gutachten-Akte detail page via SPA routing.
 * Returns null when the linked akte cannot be resolved.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { FileSearch, ExternalLink, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { aktenIdentifier, buildAkteHref } from '@/lib/akten-link';

interface LinkedGutachtenAkte {
  id: string;
  akten_nummer: string | null;
  akten_nr: string | null;
  kunde_name: string | null;
}

export default function GutachtenCrossLinkPill({ gutachtenAkteId }: { gutachtenAkteId: string }) {
  const [linked, setLinked] = useState<LinkedGutachtenAkte | null>(null);
  const [loading, setLoading] = useState(true);
  const [, navigate] = useLocation();
  const lastFetchedId = useRef<string | null>(null);

  useEffect(() => {
    if (!gutachtenAkteId || lastFetchedId.current === gutachtenAkteId) return;
    lastFetchedId.current = gutachtenAkteId;

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await supabase
          .from('akten')
          .select('id, akten_nummer, akten_nr, kunde_name')
          .eq('id', gutachtenAkteId)
          .eq('business_unit', 'gutachten')
          .maybeSingle();
        if (cancelled) return;
        setLinked((data as LinkedGutachtenAkte | null) ?? null);
      } catch {
        if (!cancelled) setLinked(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gutachtenAkteId]);

  if (loading && !linked) {
    return (
      <div
        data-testid="gutachten-pill-loading"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-[12px] text-gray-500"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Gutachten-Akte wird geladen...
      </div>
    );
  }

  if (!linked) return null;

  const identifier = aktenIdentifier(linked);
  if (!identifier) return null;

  const href = buildAkteHref('gutachten', identifier);

  return (
    <a
      data-testid="gutachten-cross-link-pill"
      href={href}
      onClick={(e) => {
        e.preventDefault();
        navigate(href);
      }}
      className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 transition-colors group max-w-full"
    >
      <span className="w-6 h-6 rounded-md bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
        <FileSearch className="w-3.5 h-3.5" />
      </span>
      <span className="flex items-baseline gap-1.5 min-w-0">
        <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
          Aus Gutachten:
        </span>
        <span className="text-[12px] font-mono font-semibold truncate">
          {identifier}
        </span>
        {linked.kunde_name && (
          <span className="text-[11px] opacity-70 truncate hidden sm:inline">
            · {linked.kunde_name}
          </span>
        )}
      </span>
      <ExternalLink className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 flex-shrink-0" />
    </a>
  );
}
